import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Circle, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useSelector } from "react-redux";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

// Setting the icon options globally for Leaflet
L.Marker.prototype.options.icon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41], // size of the icon
  iconAnchor: [12, 41], // point of the icon which will correspond to marker's location
  popupAnchor: [1, -34], // point from which the popup should open relative to the iconAnchor
  shadowSize: [41, 41], // size of the shadow
});

const ExecutionMap = () => {
  const { ExecutionData } = useSelector((state) => state.execution);
  const [features, setFeatures] = useState([]);
  const mapref = useRef();

  useEffect(() => {
    if (ExecutionData && ExecutionData.GeoTable) {
      setFeatures([]);
      ExecutionData?.GeoTable.forEach((zipcode) => {
        zipcodeToCoordinates(
          zipcode.zipCode,
          zipcode.Latitude,
          zipcode.Longitude,
          zipcode.CityName,
          zipcode.RoomRental,
          zipcode.Decor,
          zipcode.RDD,
          zipcode.Vendor,
          zipcode.EventDay,
          zipcode.ReschedulingFee,
          zipcode.BeverageFee,
          zipcode.Other
        );
      });
    }
  }, [ExecutionData]);

  async function zipcodeToCoordinates(
    zipCode,
    Latitude,
    Longitude,
    CityName,
    RR,
    D,
    RD,
    V,
    ED,
    RF,
    BF,
    OT
  ) {
    try {
      let displayname = CityName;
      let coordinates = { latitude: Latitude, longitude: Longitude };
      let reversedCoordinates = [coordinates.longitude, coordinates.latitude];
      let id = Math.random();
      let RoomRental = RR;
      let Decor = D;
      let RDD = RD;
      let Vendor = V;
      let EventDayCharges = ED;
      let Rescheduling = RF;
      let Beverage = BF;
      let Other = OT;

      setFeatures((prevFeatures) => [
        ...prevFeatures,
        createFeature(
          displayname,
          reversedCoordinates,
          id,
          RoomRental,
          Decor,
          RDD,
          Vendor,
          EventDayCharges,
          Rescheduling,
          Beverage,
          Other
        ),
      ]);
    } catch (error) {
      console.log("An error occurred while fetching coordinates.");
    }
  }

  function createFeature(
    displayname,
    reversedCoordinates,
    id,
    RR,
    D,
    RD,
    V,
    ED,
    RF,
    BF,
    OT
  ) {
    return {
      type: "Feature",
      id: id.toString(),
      properties: {
        displayname: displayname,
        id: id,
        RoomRental: RR,
        Decor: D,
        RDD: RD,
        Vendor: V,
        EventDayCharges: ED,
        Rescheduling: RF,
        Beverage: BF,
        Other: OT,
      },
      geometry: {
        type: "Point",
        coordinates: reversedCoordinates,
      },
    };
  }

  const USData = useMemo(
    () => ({
      type: "FeatureCollection",
      features: features,
    }),
    [features]
  );

  const formatNumberUS = (num) => {
    num = Number(num);
    if (num >= 1000000000) {
      // 1 billion or more
      return `$${(num / 1000000000).toFixed(2)}B`; // Billion
    } else if (num >= 1000000) {
      // 1 million to 999 million
      return `$${(num / 1000000).toFixed(2)}M`; // Million
    } else if (num >= 1000) {
      // 1 thousand to 999 thousand
      return `$${(num / 1000).toFixed(2)}K`; // Thousand
    }
    return `$${num.toFixed(2)}`; // Less than a thousand
  };

  return (
    <>
      <div className="card">
        <div className="card-body">
          <div id="map">
            <MapContainer
              style={{ height: "500px", width: "100%" }}
              center={[35.7596, -79.0193]}
              zoom={8.5}
              ref={mapref}
            >
              {USData &&
                USData?.features.map((feature) => (
                  <Circle
                    key={feature.id}
                    center={feature?.geometry?.coordinates.reverse()}
                    radius={feature.properties.RoomRental > 80 ? 2500 : 3000}
                    pathOptions={{
                      color: "#fc6075",
                      fillColor: "#fc6075",
                      fillOpacity: 0.4,
                    }}
                  >
                    <Popup>
                      <h4>{feature.properties.displayname}</h4>
                      {feature.properties.RoomRental != undefined && (
                        <div>
                          <span style={{ marginRight: "5px" }}></span>
                          Room Rental:{" "}
                          <b>{formatNumberUS(feature.properties.RoomRental)}</b>
                        </div>
                      )}

                      {feature.properties.Decor != undefined && (
                        <div>
                          <span style={{ marginRight: "5px" }}></span>
                          Decor:{" "}
                          <b>{formatNumberUS(feature.properties.Decor)}</b>
                        </div>
                      )}
                      {feature.properties.RDD != undefined && (
                        <div>
                          <span style={{ marginRight: "5px" }}></span>
                          RDD: <b>{formatNumberUS(feature.properties.RDD)}</b>
                        </div>
                      )}
                      {feature.properties.Vendor != undefined && (
                        <div>
                          <span style={{ marginRight: "5px" }}></span>
                          Vendor:{" "}
                          <b>{formatNumberUS(feature.properties.Vendor)}</b>
                        </div>
                      )}
                      {feature.properties.EventDayCharges != undefined && (
                        <div>
                          <span style={{ marginRight: "5px" }}></span>
                          EventDay Charges:{" "}
                          <b>
                            {formatNumberUS(feature.properties.EventDayCharges)}
                          </b>
                        </div>
                      )}
                      {feature.properties.Rescheduling != undefined && (
                        <div>
                          <span style={{ marginRight: "5px" }}></span>
                          Rescheduling Fee:{" "}
                          <b>
                            {formatNumberUS(feature.properties.Rescheduling)}
                          </b>
                        </div>
                      )}
                      {feature.properties.Beverage != undefined && (
                        <div>
                          <span style={{ marginRight: "5px" }}></span>
                          Beverage Fee:{" "}
                          <b>{formatNumberUS(feature.properties.Beverage)}</b>
                        </div>
                      )}
                      {feature.properties.Other != undefined && (
                        <div>
                          <span style={{ marginRight: "5px" }}></span>
                          Other:{" "}
                          <b>{formatNumberUS(feature.properties.Other)}</b>
                        </div>
                      )}
                    </Popup>
                  </Circle>
                ))}

              <TileLayer url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png" />
              <GeoJSON data={USData} />
            </MapContainer>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExecutionMap;
